import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const PIE_COLORS = ["#002FA7", "#334155", "#64748B", "#94A3B8", "#CBD5E1", "#E2E8F0", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444"];

export default function PubDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const didFetch = useRef(false);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    (async () => {
      try {
        const { data: d } = await API.get("/publisher/dashboard");
        setData(d);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading dashboard...</div>;

  const snap = data?.snapshot || {};
  const balance = data?.account_balance || {};

  return (
    <div className="p-8" data-testid="publisher-dashboard">
      <div className="mb-8">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Overview / All Time
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-2">
          Lifetime totals. Earnings equals Open + Confirmed commissions
          below — use Reports for a date-filtered view.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Snapshot — bento */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Hero Earnings */}
            <div
              className="col-span-2 md:row-span-2 relative overflow-hidden bg-[#002FA7] text-white rounded-md p-6 flex flex-col justify-between min-h-[180px]"
              data-testid="metric-earnings"
            >
              <div
                className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                }}
              />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-white/70 mb-1">
                  Earnings
                </div>
                <div className="text-[11px] text-white/50">All time</div>
              </div>
              <div className="relative">
                {/* Sized down a step at md. The hero shares its row with
                    the tile grid, so at tablet the 5xl mono clipped
                    $213,320.25 to $213,320 — the page's headline earnings
                    figure, silently missing its cents. */}
                <div className="font-mono text-3xl md:text-4xl lg:text-5xl font-bold
                                leading-none tracking-tight break-words tabular-nums">
                  ${snap.total_earnings?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
                </div>
                <div className="mt-4 h-px w-12 bg-white/40" />
                <div className="mt-3 text-xs text-white/70">
                  {snap.total_sales?.toLocaleString() || 0} sales · {snap.total_clicks?.toLocaleString() || 0} clicks
                </div>
              </div>
            </div>

            {[
              { label: "Clicks", value: snap.total_clicks?.toLocaleString() || "0" },
              // Sale Amount = the GMV this publisher drove, on the same
              // excl-rejected basis as Earnings and Sales above. That is
              // also what Reports → Sale Amount shows, so the two screens
              // carry the same number rather than differing by the rejected
              // total (which for one live publisher is $305k).
              {
                label: "Sale Amount",
                value: `$${(snap.total_sale_amount || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                hint: "excl. rejected",
              },
              // "excl. rejected" is load-bearing, not decoration. Sales here
              // counts every non-REVERSED action, while Reports → Total
              // Actions counts the gross set (Approved + Pending + Rejected),
              // so the two pages legitimately differ by exactly the rejected
              // count and a publisher comparing them has no other way to see
              // why. Reports says so by showing its Rejected tile alongside
              // the total; this is the same disclosure for this page.
              {
                label: "Sales",
                value: snap.total_sales?.toLocaleString() || "0",
                hint: "excl. rejected",
              },
              { label: "CR", value: `${snap.cr || 0}%`, hint: "sales ÷ clicks" },
              { label: "eCPC", value: snap.ecpc?.toFixed(5) || "0.00000" },
              {
                label: "AOV",
                value: `$${(snap.aov || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                hint: "sale amount ÷ sales",
              },
            ].map((m) => (
              <div
                key={m.label}
                className="border border-slate-200 rounded-md p-4 bg-white hover:border-slate-300 transition-colors"
                data-testid={`metric-${m.label.toLowerCase()}`}
              >
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
                  {m.label}
                </div>
                {/* Responsive, not a flat text-xl. These tiles sit in a
                    4-up grid, so at tablet width each is ~110px — a
                    13-character money value like $2,310,458.59 rendered
                    mono at text-xl overflowed and clipped to $2,310,458,
                    silently dropping the cents off a financial figure.
                    `break-words` is the backstop for a value longer than
                    any size we pick: wrapping is ugly, truncating money
                    is wrong. */}
                <div className="text-base lg:text-xl font-bold font-mono
                                text-slate-900 tabular-nums break-words">
                  {m.value}
                </div>
                {m.hint && (
                  <div className="text-[10px] text-slate-400 mt-1">{m.hint}</div>
                )}
              </div>
            ))}
          </div>

          {/* Daily Chart */}
          {data?.daily_chart?.length > 0 && (
            <div className="border border-slate-200 rounded-md p-6">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-heading text-base font-semibold text-slate-900 tracking-tight">Day of Week</h2>
                <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400">Volume</span>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.daily_chart}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                  <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: "4px", fontSize: "12px" }} cursor={{ fill: "#F1F5F9" }} />
                  <Bar dataKey="count" fill="#002FA7" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Country Chart */}
          {data?.country_chart?.length > 0 && (
            <div className="border border-slate-200 rounded-md p-6">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-heading text-base font-semibold text-slate-900 tracking-tight">Country</h2>
                <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400">Earnings Share</span>
              </div>
              <div className="flex items-center gap-8">
                <ResponsiveContainer width="50%" height={220}>
                  <PieChart>
                    <Pie data={data.country_chart} dataKey="value" nameKey="country" cx="50%" cy="50%" outerRadius={90} label={({ country, percent }) => `${country}: ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {data.country_chart.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {data.country_chart.map((c, i) => (
                    <div key={c.country} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-slate-600">{c.country}:</span>
                      <span className="font-mono text-slate-900">${c.value.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Account Balance */}
          <div className="border border-slate-200 rounded-md p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Account Balance</h3>
            <div className="space-y-3">
              {[
                { label: "Open Commissions Count", value: balance.open_commissions_count || 0 },
                { label: "Open Commissions Sum", value: (balance.open_commissions_sum || 0).toFixed(2) },
                { label: "Confirmed Commissions Count", value: balance.confirmed_commissions_count || 0 },
                { label: "Confirmed Commissions Sum", value: (balance.confirmed_commissions_sum || 0).toFixed(2) },
                { label: "Rejection Rate %", value: balance.rejection_rate || 0 },
                { label: "Paid Commissions Count", value: balance.paid_commissions_count || 0 },
                { label: "Paid Commissions Sum", value: (balance.paid_commissions_sum || 0).toFixed(2) },
              ].map((item) => (
                <div key={item.label} className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-xs text-slate-500">{item.label}</span>
                  <span className="font-mono text-sm font-semibold text-[#002FA7] bg-[#002FA7]/5 px-2 py-0.5 rounded">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Estimated Next Payment */}
          <div className="border border-slate-200 rounded-md p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Estimated Next Payment*</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-1.5 border-b border-slate-100">
                <span className="text-xs text-slate-500">Commission Count</span>
                <span className="font-mono text-sm font-semibold text-[#002FA7] bg-[#002FA7]/5 px-2 py-0.5 rounded">0</span>
              </div>
              <div className="flex justify-between items-center py-1.5">
                <span className="text-xs text-slate-500">Commission Sum</span>
                <span className="font-mono text-sm font-semibold text-[#002FA7] bg-[#002FA7]/5 px-2 py-0.5 rounded">0.00</span>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-3">*Estimated based on confirmed commissions</p>
          </div>

          {/* Quick Info */}
          <div className="border border-slate-200 rounded-md p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Your Info</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">SubID</span><span className="font-mono font-bold text-slate-900">{user?.sub_id}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Active Brands</span><span className="font-mono text-slate-900">{data?.active_brands_count || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Paused Brands</span><span className="font-mono text-slate-900">{data?.paused_brands_count || 0}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
